import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Eye, Globe, Moon, Sun, Monitor, Contrast, Shield, Trash2, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSimpleLanguage } from '../contexts/SimpleLanguageContext';
import { useNotifications } from '../contexts/NotificationContext';

const Settings = () => {
  const { logout, isAdmin, user, token } = useAuth();
  const { theme, changeTheme, loading: themeLoading } = useTheme();
  const { currentLanguage, availableLanguages, changeLanguage, loading: languageLoading } = useSimpleLanguage();
  const { 
    pushSupported, 
    pushSubscribed, 
    subscribeToPush, 
    unsubscribeFromPush,
    requestNotificationPermission 
  } = useNotifications();
  
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Settings state
  const [settings, setSettings] = useState({
    theme: 'light',
    language: 'en',
    emailNotifications: true,
    pushNotifications: false,
    marketingEmails: false,
    publicProfile: false,
    showAdult: false,
    searchHistory: true,
    autoplay: true
  });
  
  const [loading, setLoading] = useState({
    preferences: false,
    deleteAccount: false
  });
  
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Release-digest subscription is a separate list (email_signups), not part of
  // /preferences, so it has its own state + endpoint (/me/digest).
  const [digestSubscribed, setDigestSubscribed] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    fetch(`${API_BASE}/me/digest`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setDigestSubscribed(d.subscribed))
      .catch(() => {});
  }, [user, token, API_BASE]);

  const toggleDigest = async () => {
    setDigestBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/me/digest`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscribed: !digestSubscribed }),
      });
      if (resp.ok) {
        const d = await resp.json();
        setDigestSubscribed(d.subscribed);
        setSuccess(d.subscribed ? 'Subscribed to weekly release alerts.' : 'Unsubscribed from release alerts.');
      }
    } catch (e) {
      // ignore; toggle stays as-is
    }
    setDigestBusy(false);
  };

  // Load user preferences on component mount
  useEffect(() => {
    loadUserPreferences();
  }, [user, token]);

  // Sync theme from context to local state
  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      theme: theme
    }));
  }, [theme]);

  // Load user preferences from backend
  const loadUserPreferences = async () => {
    if (!user || !token) return;
    
    try {
      setLoading(prev => ({ ...prev, preferences: true }));
      const response = await fetch(`${API_BASE}/preferences/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const preferences = await response.json();
        setSettings(prev => ({
          ...prev,
          theme: preferences.theme,
          language: preferences.language,
          emailNotifications: preferences.email_notifications,
          marketingEmails: preferences.marketing_emails,
          publicProfile: preferences.public_profile,
          showAdult: preferences.show_adult,
          pushNotifications: pushSubscribed
        }));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(prev => ({ ...prev, preferences: false }));
    }
  };

  // Handle settings change
  const handleSettingChange = async (key, value) => {
    // Handle theme changes separately through ThemeContext
    if (key === 'theme') {
      const success = await changeTheme(value);
      if (success) {
        setSuccess('Settings saved successfully!');
        setTimeout(() => setSuccess(''), 2000);
      }
      return;
    }
    
    // Language functionality temporarily removed
    if (key === 'language') {
      return;
    }
    
    // Handle push notifications separately
    if (key === 'pushNotifications') {
      if (value && pushSupported) {
        const hasPermission = await requestNotificationPermission();
        if (hasPermission) {
          const success = await subscribeToPush();
          if (success) {
            setSettings(prev => ({ ...prev, [key]: value }));
            setSuccess('Push notifications enabled successfully!');
            setTimeout(() => setSuccess(''), 2000);
          } else {
            setSuccess('Failed to enable push notifications. Please try again.');
            setTimeout(() => setSuccess(''), 3000);
          }
        } else {
          setSuccess('Please enable notifications in your browser settings.');
          setTimeout(() => setSuccess(''), 3000);
        }
      } else if (!value && pushSubscribed) {
        const success = await unsubscribeFromPush();
        if (success) {
          setSettings(prev => ({ ...prev, [key]: value }));
          setSuccess('Push notifications disabled successfully!');
          setTimeout(() => setSuccess(''), 2000);
        }
      }
      return;
    }
    
    // Update local state immediately
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Save other preferences to backend
    if (user && token) {
      try {
        const updateData = { [key === 'emailNotifications' ? 'email_notifications' :
                             key === 'marketingEmails' ? 'marketing_emails' :
                             key === 'publicProfile' ? 'public_profile' :
                             key === 'showAdult' ? 'show_adult' : key]: value };
        
        const response = await fetch(`${API_BASE}/preferences/`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          setSuccess('Settings saved successfully!');
          setTimeout(() => setSuccess(''), 2000);
        } else {
          throw new Error('Failed to save preferences');
        }
      } catch (error) {
        console.error('Error saving preferences:', error);
        setSuccess('Operation failed. Please try again.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } else {
      // Save to localStorage for non-authenticated users
      localStorage.setItem(`videbits-${key}`, JSON.stringify(value));
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 2000);
    }
  };

  // Handle clear data
  const handleClearSearchHistory = () => {
    localStorage.removeItem('videbit-search-history');
    setSuccess('Search history cleared!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleClearWatchlistData = () => {
    localStorage.removeItem('videbit-watchlist');
    localStorage.removeItem('videbit-likes');
    localStorage.removeItem('videbit-bookmarks');
    setSuccess('All watchlist data cleared!');
    setTimeout(() => setSuccess(''), 2000);
  };

  // Handle account deletion (placeholder - disabled)
  const handleDeleteAccount = async () => {
    // Feature disabled - coming soon
    alert('Account deletion feature is coming soon');
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <div className="h-6 w-px bg-gray-300"></div>
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
            {isAdmin && (
              <div className="flex items-center space-x-1 bg-videbits-100 px-2 py-1 rounded-full">
                <Shield className="w-3 h-3 text-videbits-600" />
                <span className="text-xs text-videbits-600 font-medium">Admin</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Admin Dashboard Link - Only show for admins */}
          {isAdmin && (
            <div className="bg-gradient-to-r from-videbits-50 to-pink-50 rounded-lg border-2 border-videbits-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <Shield className="w-6 h-6 text-videbits-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Admin Tools</h2>
                  </div>
                  <p className="text-gray-700 mb-4">
                    User management has been moved to the dedicated Admin Dashboard for better organization and more powerful features.
                  </p>
                  <Link
                    to="/admin"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    <span>Open Admin Dashboard</span>
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Eye className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Theme
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { key: 'light', label: 'Light', icon: Sun },
                    { key: 'dark', label: 'Dark', icon: Moon },
                    { key: 'grey', label: 'Grey', icon: Contrast },
                    { key: 'system', label: 'System', icon: Monitor }
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => handleSettingChange('theme', key)}
                      disabled={themeLoading}
                      className={`flex items-center space-x-2 p-3 rounded-lg border transition-colors disabled:opacity-50 ${
                        settings.theme === key
                          ? 'border-videbits-300 bg-videbits-50 text-videbits-700'
                          : 'border-videbits-200 text-videbits-700 hover:bg-videbits-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
            </div>
          </div>

          {/* Notification Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Bell className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
            </div>
            
            <div className="space-y-4">
              {[
                {
                  key: 'emailNotifications',
                  label: 'Email Notifications',
                  description: 'Receive updates about your account and activity',
                  implemented: true
                },
                {
                  key: 'pushNotifications',
                  label: 'Push Notifications',
                  description: pushSupported 
                    ? 'Get notified about new features and recommendations' 
                    : 'Not supported in your browser',
                  implemented: pushSupported
                },
                {
                  key: 'marketingEmails',
                  label: 'Marketing Emails',
                  description: 'Receive newsletters and promotional content',
                  implemented: true
                }
              ].map(({ key, label, description, implemented }) => (
                <div key={key} className={`flex items-center justify-between py-3 ${!implemented ? 'opacity-50' : ''}`}>
                  <div>
                    <h3 className={`text-sm font-medium ${implemented ? 'text-gray-900' : 'text-gray-500'}`}>{label}</h3>
                    <p className={`text-xs ${implemented ? 'text-gray-500' : 'text-gray-400'}`}>{description}</p>
                  </div>
                  <button
                    onClick={() => implemented && handleSettingChange(key, !settings[key])}
                    disabled={!implemented}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      implemented ? (settings[key] ? 'bg-videbits-600' : 'bg-videbits-200') : 'bg-gray-100 cursor-not-allowed'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        implemented && settings[key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}

              {/* Weekly release digest (email_signups, double opt-in skipped for
                  logged-in verified users) */}
              <div className="flex items-center justify-between py-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Weekly Release Alerts</h3>
                  <p className="text-xs text-gray-500">A weekly email of upcoming film &amp; TV releases</p>
                </div>
                <button
                  onClick={toggleDigest}
                  disabled={digestBusy}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    digestSubscribed ? 'bg-videbits-600' : 'bg-videbits-200'
                  } ${digestBusy ? 'opacity-50' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      digestSubscribed ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Shield className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
            </div>
            
            <div className="space-y-4">
              {[
                {
                  key: 'publicProfile',
                  label: 'Public Profile',
                  description: 'Allow others to see your profile and activity'
                },
                {
                  key: 'searchHistory',
                  label: 'Save Search History',
                  description: 'Keep track of your searches for better recommendations'
                }
              ].map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between py-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{label}</h3>
                    <p className="text-xs text-gray-500">{description}</p>
                  </div>
                  <button
                    onClick={() => handleSettingChange(key, !settings[key])}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings[key] ? 'bg-videbits-600' : 'bg-videbits-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings[key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Eye className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Content</h2>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="pr-6">
                <h3 className="text-sm font-medium text-gray-900">Show adult titles</h3>
                <p className="text-xs text-gray-500">
                  Include adult titles in your search results. Off by default.
                  This applies to search only — browse, recommendations and the
                  public pages never show adult titles.
                </p>
              </div>
              <button
                onClick={() => handleSettingChange('showAdult', !settings.showAdult)}
                role="switch"
                aria-checked={settings.showAdult}
                aria-label="Show adult titles in search results"
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  settings.showAdult ? 'bg-videbits-600' : 'bg-videbits-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.showAdult ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Data Management */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Globe className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Data Management</h2>
            </div>
            
            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Search History</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Clear your saved search history and recommendations
                </p>
                <button
                  onClick={handleClearSearchHistory}
                  className="px-3 py-1 text-sm border border-videbits-300 text-videbits-700 rounded-md hover:bg-videbits-50 transition-colors"
                >
                  Clear Search History
                </button>
              </div>
              
              <div className="border-b border-gray-100 pb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Watchlist Data</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Remove all saved movies, likes, and bookmarks
                </p>
                <button
                  onClick={handleClearWatchlistData}
                  className="px-3 py-1 text-sm border border-videbits-300 text-videbits-700 rounded-md hover:bg-videbits-50 transition-colors"
                >
                  Clear Watchlist Data
                </button>
              </div>
              
              <div className="opacity-50">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Export Data</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Download a copy of your account data and activity (Coming Soon)
                </p>
                <button
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed"
                  disabled
                  onClick={() => alert('Data export feature is coming soon')}
                >
                  Request Data Export
                </button>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Trash2 className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
            </div>
            
            <div className="space-y-4">
              <div className="border-b border-red-100 pb-4">
                <h3 className="text-sm font-medium text-red-900 mb-2">Sign Out</h3>
                <p className="text-xs text-red-600 mb-3">
                  Sign out of your account on this device
                </p>
                <button
                  onClick={logout}
                  className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors"
                >
                  Sign Out
                </button>
              </div>
              
              <div className="opacity-50">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Delete Account</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Permanently delete your account and all associated data (Coming Soon)
                </p>
                <button
                  disabled
                  className="px-3 py-1 text-sm bg-gray-300 text-gray-500 rounded-md cursor-not-allowed"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-videbits-300 text-videbits-700 rounded-lg hover:bg-videbits-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading.deleteAccount}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading.deleteAccount ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
